import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button } from 'antd';
import { BellOutlined, EditOutlined } from '@ant-design/icons';
import { getCurrencySymbol, getCurrencyCode } from 'utils/currencyUtil';
import { formatCurrency } from 'pages/InvoicesAndStatements/helper';
import useTranslation from 'utils/hooks/useTranslation';

import styles from './style.module.scss';

const PRESETS = [25, 50, 100];

const LowBalanceAlert = ({ threshold = 10, onSave }) => {
  const { translateText } = useTranslation();
  const { currentCorporation } = useSelector(state => state.user);
  const country = currentCorporation?.country;
  const symbol = getCurrencySymbol(country);
  const format = value => formatCurrency(getCurrencyCode(country), value);

  const [saved, setSaved] = useState(threshold);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(threshold);
  const [custom, setCustom] = useState('');

  const startEditing = () => {
    setDraft(saved);
    setCustom('');
    setIsEditing(true);
  };

  const handleSave = () => {
    const parsedCustom = parseFloat(custom);
    const value = custom !== '' && !Number.isNaN(parsedCustom) ? parsedCustom : draft;
    setSaved(value);
    setIsEditing(false);
    if (onSave) onSave(value);
  };

  return (
    <div className={styles.card} data-testid="walletLowBalanceAlert">
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderIcon}>
          <BellOutlined />
        </span>
        {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.title')}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardCopy}>
          {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.copy')}
        </div>

        {isEditing ? (
          <div data-testid="walletLowBalanceAlertEditPanel">
            <div className={styles.amountRow}>
              {PRESETS.map(preset => (
                <button
                  type="button"
                  key={preset}
                  className={classNames(styles.amountPreset, {
                    [styles.selected]: custom === '' && draft === preset,
                  })}
                  onClick={() => {
                    setDraft(preset);
                    setCustom('');
                  }}
                >
                  {format(preset)}
                </button>
              ))}
              <input
                className={styles.amountOther}
                value={custom}
                onChange={event => setCustom(event.target.value)}
                placeholder={translateText(
                  'corporateAccounts.wallet.dashboard.lowBalanceAlert.otherPlaceholder',
                  { symbol },
                )}
                aria-label={translateText(
                  'corporateAccounts.wallet.dashboard.lowBalanceAlert.otherPlaceholder',
                  { symbol },
                )}
              />
            </div>
            <div className={styles.editActions}>
              <Button
                type="primary"
                className={styles.saveAlert}
                onClick={handleSave}
                data-testid="walletLowBalanceAlertSave"
              >
                {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.save')}
              </Button>
              <Button
                className={styles.cancelAlert}
                onClick={() => setIsEditing(false)}
                data-testid="walletLowBalanceAlertCancel"
              >
                {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.alertSavedRow}>
            <div className={styles.alertAmount}>{format(saved)}</div>
            <span
              className={styles.editLink}
              onClick={startEditing}
              data-testid="walletLowBalanceAlertEdit"
            >
              <EditOutlined />
              {translateText('corporateAccounts.wallet.dashboard.lowBalanceAlert.edit')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LowBalanceAlert;
